import { createSlice } from '@reduxjs/toolkit';
import { ReducerStatus } from 'utils/constants';

import {
  createVideoSubQuest,
  createInteractiveContentSubQuest,
  createQuizSubQuest,
  createQuizSubQuestQuestions,
  createNarrativeSubQuest,
  createFileSubmissionSubQuest,
  updateVideoSubQuest,
  updateInteractiveContentSubQuest,
  updateQuizSubQuest,
  updateQuizSubQuestQuestions,
  updateNarrativeSubQuest,
  updateFileSubmissionSubQuest,
  updateSubQuestAction,
  updateSubQuestOrderNo,
  removeSubQuest,
  getSubQuestById,
  getLatestFileSubmissionSession,
  updateFileSubmissionSession,
  getSubQuests,
  detachSubQuestById,
  attachSubQuestsByIds,
  getRelatedCourseQuestBySubQuest,
} from './subquest-action';
import updatePublishStatusAction from '../common/publishEntity/publishEntity-action';

const subquestSlice = createSlice({
  name: 'subquest',
  initialState: {
    uploadProgress: 0,
    questSubQuests: [],
    subQuest: null,
    questId: null,
    fileSubmissionSession: null,
    subQuests: [],
    metaData: null,
    getSubQuestsStatus: ReducerStatus.IDLE,
    getSubQuest: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    getLatestFileSubmissionSession: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    createVideo: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateSubQuest: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    createInteractiveContent: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    createQuiz: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    createQuizQuestions: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateVideo: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateInteractiveContent: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateQuiz: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateQuizQuestions: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    removeSubQuest: {
      subQuestId: null,
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateSubQuestOrderNo: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    createNarrative: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateNarrative: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    createFileSubmission: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateFileSubmission: {
      modal: false,
      id: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateFileSubmissionSession: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    detachSubQuest: {
      modal: false,
      id: null,
      questId: null,
      courseId: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
    attachSubQuest: {
      modal: false,
      id: null,
      questId: null,
      courseId: null,
      count: 0,
      status: ReducerStatus.IDLE,
      error: null,
    },
    showSubQuestScore: false,
    relatedCourseQuests: {
      courseName: null,
      questName: null,
      courseQuests: [],
      status: ReducerStatus.IDLE,
      error: null,
    },
  },
  reducers: {
    addSubQuest(state, action) {
      state.questSubQuests = [...state.questSubQuests, action.payload];
    },
    setUploadProgress(state, action) {
      state.uploadProgress = action.payload;
    },
    setCourseId(state, action) {
      state.courseId = action.payload;
    },
    setQuestId(state, action) {
      state.questId = action.payload;
    },
    setQuest(state, action) {
      state.quest = action.payload;
    },
    setSubQuestIdForRemoval(state, action) {
      state.removeSubQuest.subQuestId = action.payload;
    },
    setDetachSubQuest(state, action) {
      state.detachSubQuest = {
        ...state.detachSubQuest,
        ...action.payload,
      };
    },
    setAttachSubQuest(state, action) {
      state.attachSubQuest = {
        ...state.attachSubQuest,
        ...action.payload,
      };
    },
    setCreateFileSubmission(state, action) {
      state.createFileSubmission = {
        ...state.createFileSubmission,
        ...action.payload,
      };
    },
    setUpdateFileSubmission(state, action) {
      state.updateFileSubmission = {
        ...state.updateFileSubmission,
        ...action.payload,
      };
    },
    setUpdateFileSubmissionSession(state, action) {
      state.updateFileSubmissionSession = {
        ...state.updateFileSubmissionSession,
        ...action.payload,
      };
    },
    setRelatedCourseQuests(state, action) {
      state.relatedCourseQuests = {
        ...state.relatedCourseQuests,
        ...action.payload,
      };
    },
    updateFileSubmissionSession(state, action) {
      state.fileSubmissionSession = {
        ...state.fileSubmissionSession,
        ...action.payload,
      };
    },
    arrangeOrderNo(state, action) {
      const { questId, subQuests } = action.payload;

      state.questSubQuests = state.questSubQuests.map((questSubQuest) =>
        questSubQuest.id === questId
          ? { ...questSubQuest, subQuests }
          : questSubQuest
      );
    },
    toggleVideoUpdateModal(state, action) {
      state.updateVideo.modal = action.payload;
      if (!action.payload) state.getSubQuest.status = ReducerStatus.IDLE;
    },
    toggleInteractiveContentUpdateModal(state, action) {
      state.updateInteractiveContent.modal = action.payload;
      if (!action.payload) state.getSubQuest.status = ReducerStatus.IDLE;
    },
    toggleNarrativeUpdateModal(state, action) {
      state.updateNarrative.modal = action.payload;
      if (!action.payload) state.getSubQuest.status = ReducerStatus.IDLE;
    },
    toggleFileSubmissionUpdateModal(state, action) {
      state.updateFileSubmission.modal = action.payload;
      if (!action.payload) state.getSubQuest.status = ReducerStatus.IDLE;
    },
    toggleQuizUpdateModal(state, action) {
      state.updateQuiz.modal = action.payload;
      if (!action.payload) state.getSubQuest.status = ReducerStatus.IDLE;
    },
    toggleQuizCreateModal(state, action) {
      state.createQuiz.modal = action.payload;
    },
    toggleRemoveModal(state, action) {
      state.removeSubQuest.modal = action.payload;
    },
    resetVideoUpdateStatus(state) {
      state.updateVideo.status = ReducerStatus.IDLE;
      state.editVideoSubQuestMode = false;
    },
    resetInteractiveContentUpdateStatus(state) {
      state.updateInteractiveContent.status = ReducerStatus.IDLE;
      state.editVideoSubQuestMode = false;
    },
    resetQuizUpdateStatus(state) {
      state.updateQuiz.status = ReducerStatus.IDLE;
      state.editQuizSubQuestMode = false;
    },
    resetRemoveStatus(state) {
      state.removeSubQuest.status = ReducerStatus.IDLE;
    },
    resetCreateVideoStatus(state) {
      state.createVideo.status = ReducerStatus.IDLE;
    },
    resetUpdateNarrativeStatus(state) {
      state.updateNarrative.status = ReducerStatus.IDLE;
    },
    resetCreateVideo(state) {
      state.createVideo = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetCreateInteractiveContent(state) {
      state.createInteractiveContent = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetCreateQuiz(state) {
      state.createQuiz = {
        ...state.createQuiz,
        status: ReducerStatus.IDLE,
        error: null,
      };

      state.createQuizQuestions = {
        ...state.createQuizQuestions,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },

    resetCreateNarrative(state) {
      state.createNarrative = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetCreateFileSubmission(state) {
      state.createFileSubmission = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetUpdateFileSubmission(state) {
      state.updateFileSubmission = {
        modal: false,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetVideoUpdate(state) {
      state.updateVideo = {
        modal: false,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetInteractiveContentUpdate(state) {
      state.updateInteractiveContent = {
        modal: false,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetQuizUpdate(state) {
      state.updateQuiz = {
        modal: false,
        status: ReducerStatus.IDLE,
        error: null,
      };
      state.updateQuizQuestions = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetUpdateNarrative(state) {
      state.updateNarrative = {
        modal: false,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetUpdateSubQuest(state) {
      state.updateSubQuest = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    resetSubQuestState(state) {
      state.subQuest = null;
      state.getSubQuest = {
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    enableSubQuestScoreDisplay(state) {
      state.showSubQuestScore = true;
    },
    disableSubQuestScoreDisplay(state) {
      state.showSubQuestScore = false;
    },
    resetGetSubQuestsStatus(state) {
      state.getSubQuestsStatus = ReducerStatus.IDLE;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(createVideoSubQuest.pending, (state) => {
        state.createVideo.status = ReducerStatus.LOADING;
      })
      .addCase(createInteractiveContentSubQuest.pending, (state) => {
        state.createInteractiveContent.status = ReducerStatus.LOADING;
      })
      .addCase(createQuizSubQuest.pending, (state) => {
        state.createQuiz.status = ReducerStatus.LOADING;
      })
      .addCase(createQuizSubQuestQuestions.pending, (state) => {
        state.createQuizQuestions.status = ReducerStatus.LOADING;
      })
      .addCase(createNarrativeSubQuest.pending, (state) => {
        state.createNarrative.status = ReducerStatus.LOADING;
      })
      .addCase(createFileSubmissionSubQuest.pending, (state) => {
        state.createFileSubmission.status = ReducerStatus.LOADING;
      })
      .addCase(createFileSubmissionSubQuest.fulfilled, (state, action) => {
        state.createFileSubmission.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((item, index) => {
          if (item.id === action.payload.id) {
            state.questSubQuests[index].subQuests = [
              ...state.questSubQuests[index].subQuests,
              action.payload.subQuest,
            ];
          }
        });
        state.subQuest = null;
      })
      .addCase(createNarrativeSubQuest.fulfilled, (state, action) => {
        state.createNarrative.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((item, index) => {
          if (item.id === action.payload.id) {
            state.questSubQuests[index].subQuests = [
              ...state.questSubQuests[index].subQuests,
              action.payload.subQuest,
            ];
          }
        });
        state.subQuest = null;
      })
      .addCase(createVideoSubQuest.fulfilled, (state, action) => {
        state.createVideo.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((item, index) => {
          if (item.id === action.payload.id) {
            state.questSubQuests[index].subQuests = [
              ...state.questSubQuests[index].subQuests,
              action.payload.subQuest,
            ];
          }
        });
        state.subQuest = null;
      })
      .addCase(createInteractiveContentSubQuest.fulfilled, (state, action) => {
        state.createInteractiveContent.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((item, index) => {
          if (item.id === action.payload.id) {
            state.questSubQuests[index].subQuests = [
              ...state.questSubQuests[index].subQuests,
              action.payload.subQuest,
            ];
          }
        });

        state.subQuest = null;
      })
      .addCase(createQuizSubQuest.fulfilled, (state, action) => {
        state.createQuiz.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((item, index) => {
          if (item.id === action.payload.id) {
            state.questSubQuests[index].subQuests = [
              ...state.questSubQuests[index].subQuests,
              action.payload.subQuest,
            ];
          }
        });

        state.subQuest = null;
      })
      .addCase(createQuizSubQuestQuestions.fulfilled, (state) => {
        state.createQuizQuestions.status = ReducerStatus.SUCCEEDED;
      })
      .addCase(createVideoSubQuest.rejected, (state, action) => {
        state.createVideo.status = ReducerStatus.FAILED;
        state.createVideo.error = action.payload;
      })
      .addCase(createInteractiveContentSubQuest.rejected, (state, action) => {
        state.createInteractiveContent.status = ReducerStatus.FAILED;
        state.createInteractiveContent.error = action.payload;
      })
      .addCase(createQuizSubQuest.rejected, (state, action) => {
        state.createQuiz.status = ReducerStatus.FAILED;
        state.createQuiz.error = action.payload;
      })
      .addCase(createQuizSubQuestQuestions.rejected, (state, action) => {
        state.createQuizQuestions.status = ReducerStatus.FAILED;
        state.createQuizQuestions.error = action.payload;
      })
      .addCase(createNarrativeSubQuest.rejected, (state, action) => {
        state.createNarrative.status = ReducerStatus.FAILED;
        state.createNarrative.error = action.payload;
      })
      .addCase(createFileSubmissionSubQuest.rejected, (state, action) => {
        state.createFileSubmission.status = ReducerStatus.FAILED;
        state.createFileSubmission.error = action.payload;
      })
      // Fetch SubQuests
      .addCase(getSubQuests.pending, (state) => {
        state.getSubQuestsStatus = ReducerStatus.LOADING;
        state.subQuests = [];
      })
      .addCase(getSubQuests.fulfilled, (state, action) => {
        state.getSubQuestsStatus = ReducerStatus.SUCCEEDED;
        const { metaData, data } = action.payload;
        state.subQuests = data;
        state.metaData = metaData;
      })
      .addCase(getSubQuests.rejected, (state) => {
        state.getSubQuestsStatus = ReducerStatus.FAILED;
      })
      // Fetch SubQuest By Id
      .addCase(getSubQuestById.pending, (state) => {
        state.getSubQuest.status = ReducerStatus.LOADING;
      })
      .addCase(getSubQuestById.fulfilled, (state, action) => {
        state.getSubQuest.status = ReducerStatus.SUCCEEDED;
        state.subQuest = action.payload;
      })
      .addCase(getSubQuestById.rejected, (state, action) => {
        state.getSubQuest.status = ReducerStatus.FAILED;
        state.getSubQuest.error = action.payload;
      })
      // Update SubQuest
      .addCase(updateSubQuestAction.pending, (state) => {
        state.updateSubQuest.status = ReducerStatus.LOADING;
      })
      .addCase(updateSubQuestAction.fulfilled, (state, action) => {
        state.updateSubQuest.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
            if (subQuest.subQuest.id === action.payload.id) {
              state.questSubQuests[index].subQuests[subQuestIndex].subQuest =
                action.payload;
            }
          });
        });
      })
      .addCase(updateSubQuestAction.rejected, (state, action) => {
        state.updateSubQuest.status = ReducerStatus.FAILED;
        state.updateSubQuest.error = action.payload;
      })
      // Update Video
      .addCase(updateVideoSubQuest.pending, (state) => {
        state.updateVideo.status = ReducerStatus.LOADING;
      })
      .addCase(updateVideoSubQuest.fulfilled, (state, action) => {
        state.updateVideo.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
            if (subQuest.subQuest.id === action.payload.id) {
              state.questSubQuests[index].subQuests[subQuestIndex].subQuest =
                action.payload;
            }
          });
        });
        state.subQuest = null;
      })
      .addCase(updateVideoSubQuest.rejected, (state, action) => {
        state.updateVideo.status = ReducerStatus.FAILED;
        state.updateVideo.error = action.payload;
      })
      // Update Interactive Content
      .addCase(updateInteractiveContentSubQuest.pending, (state) => {
        state.updateInteractiveContent.status = ReducerStatus.LOADING;
      })
      .addCase(updateInteractiveContentSubQuest.fulfilled, (state, action) => {
        state.updateInteractiveContent.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
            if (subQuest.subQuest.id === action.payload.id) {
              state.questSubQuests[index].subQuests[subQuestIndex].subQuest =
                action.payload;
            }
          });
        });
        state.subQuest = null;
      })
      .addCase(updateInteractiveContentSubQuest.rejected, (state, action) => {
        state.updateInteractiveContent.status = ReducerStatus.FAILED;
        state.updateInteractiveContent.error = action.payload;
      })
      // Update Quiz
      .addCase(updateQuizSubQuest.pending, (state) => {
        state.updateQuiz.status = ReducerStatus.LOADING;
      })
      .addCase(updateQuizSubQuest.fulfilled, (state, action) => {
        state.updateQuiz.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
            if (subQuest.subQuest.id === action.payload.id) {
              state.questSubQuests[index].subQuests[subQuestIndex].subQuest =
                action.payload;
            }
          });
        });
      })
      .addCase(updateQuizSubQuest.rejected, (state, action) => {
        state.updateQuiz.status = ReducerStatus.FAILED;
        state.updateQuiz.error = action.payload;
      })
      // Update Quiz Question
      .addCase(updateQuizSubQuestQuestions.pending, (state) => {
        state.updateQuizQuestions.status = ReducerStatus.LOADING;
      })
      .addCase(updateQuizSubQuestQuestions.fulfilled, (state) => {
        state.updateQuizQuestions.status = ReducerStatus.SUCCEEDED;
      })
      .addCase(updateQuizSubQuestQuestions.rejected, (state, action) => {
        state.updateQuizQuestions.status = ReducerStatus.FAILED;
        state.updateQuizQuestions.error = action.payload;
      })
      // Update Narrative
      .addCase(updateNarrativeSubQuest.pending, (state) => {
        state.updateNarrative.status = ReducerStatus.LOADING;
      })
      .addCase(updateNarrativeSubQuest.fulfilled, (state, action) => {
        state.updateNarrative.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
            if (subQuest.subQuest.id === action.payload.id) {
              state.questSubQuests[index].subQuests[subQuestIndex].subQuest =
                action.payload;
            }
          });
        });
        state.subQuest = null;
      })
      .addCase(updateNarrativeSubQuest.rejected, (state, action) => {
        state.updateNarrative.status = ReducerStatus.FAILED;
        state.updateNarrative.error = action.payload;
      })
      // Update File Submission
      .addCase(updateFileSubmissionSubQuest.pending, (state) => {
        state.updateFileSubmission.status = ReducerStatus.LOADING;
      })
      .addCase(updateFileSubmissionSubQuest.fulfilled, (state, action) => {
        state.updateFileSubmission.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
            if (subQuest.subQuest.id === action.payload.subQuest.id) {
              state.questSubQuests[index].subQuests[subQuestIndex].subQuest =
                action.payload.subQuest;
            }
          });
        });
        state.subQuest = null;
      })
      .addCase(updateFileSubmissionSubQuest.rejected, (state, action) => {
        state.updateFileSubmission.status = ReducerStatus.FAILED;
        state.updateFileSubmission.error = action.payload;
      })
      // Remove SubQuest By ID
      .addCase(removeSubQuest.pending, (state) => {
        state.removeSubQuest.status = ReducerStatus.LOADING;
      })
      .addCase(removeSubQuest.fulfilled, (state, action) => {
        state.removeSubQuest.status = ReducerStatus.SUCCEEDED;

        state.questSubQuests.forEach((questSubQuest, index) => {
          state.questSubQuests[index].subQuests =
            questSubQuest.subQuests.filter(
              (subQuest) => subQuest.subQuest.id !== action.payload
            );
        });
      })
      .addCase(removeSubQuest.rejected, (state, action) => {
        state.removeSubQuest.status = ReducerStatus.FAILED;
        state.removeSubQuest.error = action.payload;
      })
      // Detach SubQuest By ID
      .addCase(detachSubQuestById.pending, (state) => {
        state.detachSubQuest.status = ReducerStatus.LOADING;
      })
      .addCase(detachSubQuestById.fulfilled, (state, action) => {
        state.detachSubQuest.status = ReducerStatus.SUCCEEDED;

        // Only filter subquests from the specific quest being detached from
        state.questSubQuests.forEach((questSubQuest, index) => {
          if (questSubQuest.id === state.detachSubQuest.questId) {
            state.questSubQuests[index].subQuests =
              questSubQuest.subQuests.filter(
                (subQuest) => subQuest.subQuest.id !== action.payload
              );
          }
        });
      })
      .addCase(detachSubQuestById.rejected, (state, action) => {
        state.detachSubQuest.status = ReducerStatus.FAILED;
        state.detachSubQuest.error = action.payload;
      })
      // Attach SubQuests By ID
      .addCase(attachSubQuestsByIds.pending, (state) => {
        state.attachSubQuest.status = ReducerStatus.LOADING;
      })
      .addCase(attachSubQuestsByIds.fulfilled, (state, action) => {
        state.attachSubQuest.status = ReducerStatus.SUCCEEDED;
        state.attachSubQuest.count = action.payload.length;

        state.questSubQuests.forEach((questSubQuest, index) => {
          if (questSubQuest.id === state.attachSubQuest.questId) {
            const currentLength = state.questSubQuests[index].subQuests.length;

            const newSubQuestsWithIds = action.payload.map((item, idx) => ({
              id: currentLength + idx + 1,
              ...item,
            }));

            state.questSubQuests[index].subQuests = [
              ...state.questSubQuests[index].subQuests,
              ...newSubQuestsWithIds,
            ];
          }
        });
      })
      .addCase(attachSubQuestsByIds.rejected, (state, action) => {
        state.attachSubQuest.status = ReducerStatus.FAILED;
        state.attachSubQuest.error = action.payload;
      })
      .addCase(getRelatedCourseQuestBySubQuest.pending, (state) => {
        state.relatedCourseQuests.status = ReducerStatus.LOADING;
      })
      .addCase(getRelatedCourseQuestBySubQuest.fulfilled, (state, action) => {
        state.relatedCourseQuests.status = ReducerStatus.SUCCEEDED;
        state.relatedCourseQuests.courseQuests = action.payload.filter(
          (item) =>
            item.courseName !== state.relatedCourseQuests.courseName ||
            item.questName !== state.relatedCourseQuests.questName
        );
      })
      .addCase(getRelatedCourseQuestBySubQuest.rejected, (state, action) => {
        state.relatedCourseQuests.status = ReducerStatus.FAILED;
        state.relatedCourseQuests.error = action.payload;
      })
      // Update SubQuest Order No;
      .addCase(updateSubQuestOrderNo.pending, (state) => {
        state.updateSubQuestOrderNo.status = ReducerStatus.LOADING;
      })
      .addCase(updateSubQuestOrderNo.fulfilled, (state) => {
        state.updateSubQuestOrderNo.status = ReducerStatus.SUCCEEDED;
      })
      .addCase(updateSubQuestOrderNo.rejected, (state, action) => {
        state.updateSubQuestOrderNo.status = ReducerStatus.FAILED;
        state.updateSubQuestOrderNo.error = action.payload;
      })
      // Get Latest File Submission Session
      .addCase(getLatestFileSubmissionSession.pending, (state) => {
        state.getLatestFileSubmissionSession.status = ReducerStatus.LOADING;
      })
      .addCase(getLatestFileSubmissionSession.fulfilled, (state, action) => {
        state.getLatestFileSubmissionSession.status = ReducerStatus.SUCCEEDED;
        state.fileSubmissionSession = action.payload;
      })
      .addCase(getLatestFileSubmissionSession.rejected, (state, action) => {
        state.getLatestFileSubmissionSession.status = ReducerStatus.FAILED;
        state.getLatestFileSubmissionSession.error = action.payload;
      })
      // Update File Submission Session
      .addCase(updateFileSubmissionSession.pending, (state) => {
        state.updateFileSubmissionSession.status = ReducerStatus.LOADING;
      })
      .addCase(updateFileSubmissionSession.fulfilled, (state, action) => {
        state.updateFileSubmissionSession.status = ReducerStatus.SUCCEEDED;
        state.fileSubmissionSession = action.payload;
      })
      .addCase(updateFileSubmissionSession.rejected, (state, action) => {
        state.updateFileSubmissionSession.status = ReducerStatus.FAILED;
        state.updateFileSubmissionSession.error = action.payload;
      })
      // Update Publish Status
      .addCase(updatePublishStatusAction.fulfilled, (state, action) => {
        const { entityType, entityId, isPublished } = action.payload;

        if (entityType === 'SubQuest') {
          if (state.subQuest && state.subQuest.id === entityId) {
            state.subQuest.isPublished = isPublished;
          }
          state.questSubQuests.forEach((questSubQuest, index) => {
            questSubQuest.subQuests.forEach((subQuest, subQuestIndex) => {
              if (subQuest.subQuest.id === entityId) {
                state.questSubQuests[index].subQuests[
                  subQuestIndex
                ].subQuest.isPublished = isPublished;
              }
            });
          });
        }
      });
  },
});

export const subquestActions = subquestSlice.actions;

export default subquestSlice;
